import { useEffect, useState } from "react";
import api, { showApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { UtensilsCrossed, Droplets, Scale, Plus, Minus, TrendingDown, TrendingUp, Minus as MinusIcon, ChevronRight } from "lucide-react";

export default function TodaySummary({ onOpenWeight }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/daily/summary"); setData(data); }
    catch (err) { showApiError(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleMeal = async (meal) => {
    setBusy(true);
    try { const { data } = await api.post("/daily/meal-toggle", { meal_type: meal }); setData(data); }
    catch (err) { showApiError(err); }
    finally { setBusy(false); }
  };
  const water = async (delta) => {
    setBusy(true);
    try { const { data } = await api.post("/daily/water", { delta_ml: delta }); setData(data); }
    catch (err) { showApiError(err); }
    finally { setBusy(false); }
  };

  if (loading) return <Skeleton className="h-28 rounded-2xl mb-6" data-testid="today-summary-loading" />;
  if (!data) return null;

  const mealsPct = data.meals_total ? Math.round((data.meals_done.length / data.meals_total) * 100) : 0;
  const waterPct = data.water_goal_ml ? Math.min(100, Math.round((data.water_ml / data.water_goal_ml) * 100)) : 0;
  const delta = data.weight_delta;

  return (
    <Card className="rounded-2xl mb-6 overflow-hidden" data-testid="today-summary">
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Riepilogo di oggi</div>
          <div className="font-heading text-lg font-semibold">{data.day_name}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x border-t">
        {/* Pasti fatti */}
        <div className="p-5 space-y-2" data-testid="summary-meals">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="h-8 w-8 rounded-xl bg-primary/10 grid place-items-center"><UtensilsCrossed className="h-4 w-4 text-primary" /></span>
            Pasti fatti
          </div>
          {data.has_diet ? (
            <>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-2xl font-bold" data-testid="summary-meals-count">{data.meals_done.length}</span>
                <span className="text-muted-foreground text-sm">/ {data.meals_total}</span>
              </div>
              <Progress value={mealsPct} className="h-2" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-0 h-auto text-primary hover:text-primary" data-testid="summary-meals-open">
                    Segna i pasti <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start" data-testid="summary-meals-popover">
                  <div className="space-y-1">
                    {data.meals.map((m) => {
                      const done = data.meals_done.includes(m);
                      return (
                        <label key={m} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted cursor-pointer" data-testid={`summary-meal-${m}`}>
                          <Checkbox checked={done} disabled={busy} onCheckedChange={() => toggleMeal(m)} data-testid={`summary-meal-check-${m}`} />
                          <span className={`text-sm ${done ? "line-through text-muted-foreground" : ""}`}>{m}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <p className="text-sm text-muted-foreground pt-1">Genera una dieta per spuntare i pasti.</p>
          )}
        </div>

        {/* Acqua */}
        <div className="p-5 space-y-2" data-testid="summary-water">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="h-8 w-8 rounded-xl bg-sky-500/10 grid place-items-center"><Droplets className="h-4 w-4 text-sky-500" /></span>
            Acqua
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-heading text-2xl font-bold" data-testid="summary-water-ml">{data.water_ml}</span>
            <span className="text-muted-foreground text-sm">/ {data.water_goal_ml} ml</span>
          </div>
          <Progress value={waterPct} className="h-2 [&>div]:bg-sky-500" />
          <div className="flex items-center gap-2 pt-0.5">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={busy || data.water_ml <= 0} onClick={() => water(-data.water_step_ml)} data-testid="summary-water-minus">
              <Minus className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground flex-1 text-center">{data.water_step_ml} ml / bicchiere</span>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={busy} onClick={() => water(data.water_step_ml)} data-testid="summary-water-plus">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Peso */}
        <div className="p-5 space-y-2" data-testid="summary-weight">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="h-8 w-8 rounded-xl bg-accent/10 grid place-items-center"><Scale className="h-4 w-4 text-accent" /></span>
            Peso
          </div>
          {data.last_weight != null ? (
            <>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-2xl font-bold" data-testid="summary-weight-value">{data.last_weight}</span>
                <span className="text-muted-foreground text-sm">kg</span>
              </div>
              <div className="text-sm flex items-center gap-1" data-testid="summary-weight-delta">
                {delta == null || delta === 0 ? (
                  <span className="text-muted-foreground flex items-center gap-1"><MinusIcon className="h-3.5 w-3.5" /> stabile</span>
                ) : delta < 0 ? (
                  <span className="text-emerald-600 flex items-center gap-1"><TrendingDown className="h-4 w-4" /> {Math.abs(delta)} kg</span>
                ) : (
                  <span className="text-orange-600 flex items-center gap-1"><TrendingUp className="h-4 w-4" /> +{delta} kg</span>
                )}
                <span className="text-muted-foreground text-xs">vs prec.</span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground pt-1">Nessun peso registrato.</p>
          )}
          <Button variant="ghost" size="sm" className="px-0 h-auto text-accent hover:text-accent" onClick={onOpenWeight} data-testid="summary-weight-open">
            Aggiorna peso <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
